import { useSearchParams } from "react-router";
import { Check, CircleSlash, Clock } from "lucide-react";
import { ActionLink } from "@/components/action-link";
import { CheckoutShell } from "@/components/checkout-shell";
import { Ledger, LedgerRow, type RowTone } from "@/components/ledger";
import { StatusBlock } from "@/components/status-block";
import { hostOf } from "@/lib/return-url";
import type { SessionState } from "@/lib/session-status";
import { useSessionPoll, type ReturnPhase } from "@/lib/use-session-poll";

/**
 * The post-payment page — where a shopper lands coming back from Stripe, and
 * the only screen in this app that anyone is anxious on.
 *
 * All of the behaviour is in @/lib/use-session-poll and the pure modules it
 * calls; this file is what the shopper reads while that runs. The one thing
 * worth knowing here is that `phase` and `lastState` are two different facts
 * and both are on screen: the page can be phase `polling` while the session
 * has already reported `paid`, because the signed receipt has not been issued
 * yet. That gap is the webhook race, it is the most common reason this page
 * is up for more than a second, and telling someone "your payment came
 * through, we're waiting on the receipt" is worth far more than a spinner.
 */

const SUPPORT_EMAIL = "support@sara.ai";

const TITLES: Record<ReturnPhase, string> = {
  polling: "Confirming payment · Sara's Books",
  success: "Payment confirmed · Sara's Books",
  expired: "Checkout expired · Sara's Books",
  abandoned: "Payment not completed · Sara's Books",
  timeout: "Still confirming · Sara's Books",
};

export function ReturnPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  // Carried through from the URL so it can be appended to the consumer's
  // return_url beside the receipt. The status response may also carry it; the
  // poll prefers whichever is present, response first.
  const orderId = params.get("order_id");

  const { phase, lastState, returnUrl, redirectUrl } = useSessionPoll(
    sessionId,
    orderId,
  );
  const returnHost = hostOf(returnUrl);

  return (
    <CheckoutShell title={TITLES[phase]}>
      {phase === "polling" && (
        <StatusBlock
          tone="waiting"
          icon={<Clock className="size-4.5" aria-hidden />}
          title="Confirming your payment"
          body={
            lastState === "paid"
              ? "Your payment came through. We’re waiting on the signed receipt that unlocks it — please keep this page open."
              : "We’re confirming your payment with our processor. This usually takes a few seconds — please keep this page open."
          }
        >
          <TransactionLedger
            phase={phase}
            lastState={lastState}
            returnHost={returnHost}
            visible={Boolean(sessionId)}
          />
        </StatusBlock>
      )}

      {phase === "success" && (
        <StatusBlock
          tone="settled"
          icon={<Check className="size-4.5" aria-hidden />}
          title="Payment confirmed"
          body={
            redirectUrl === null
              ? "Your payment is complete. You can now return to where you started."
              : returnHost === null
                ? "Taking you back…"
                : `Taking you back to ${returnHost}…`
          }
        >
          <TransactionLedger
            phase={phase}
            lastState={lastState}
            returnHost={returnHost}
            visible={Boolean(sessionId)}
          />
          {redirectUrl !== null && returnHost !== null && (
            <ActionLink href={redirectUrl}>Return to {returnHost}</ActionLink>
          )}
        </StatusBlock>
      )}

      {phase === "expired" && (
        <StatusBlock
          tone="held"
          icon={<Clock className="size-4.5" aria-hidden />}
          title="This checkout session expired"
          body="For your security, the payment session timed out before it was completed. Head back to the store and start the purchase again."
        >
          <ReturnFallback returnUrl={returnUrl} returnHost={returnHost} />
        </StatusBlock>
      )}

      {phase === "abandoned" && (
        <StatusBlock
          tone="held"
          icon={<CircleSlash className="size-4.5" aria-hidden />}
          title="Payment not completed"
          body="This checkout session was closed before payment finished. You can safely return and try again — you have not been charged."
        >
          <ReturnFallback returnUrl={returnUrl} returnHost={returnHost} />
        </StatusBlock>
      )}

      {phase === "timeout" && (
        <StatusBlock
          tone="held"
          icon={<Clock className="size-4.5" aria-hidden />}
          title="Still confirming your payment"
          body={
            <>
              This is taking longer than usual. If you completed payment, your
              receipt is still being issued and no action is lost. If the
              problem persists, contact{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-foreground underline underline-offset-4"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </>
          }
        >
          <TransactionLedger
            phase={phase}
            lastState={lastState}
            returnHost={returnHost}
            visible={Boolean(sessionId)}
          />
          {/* Deliberately NOT the generic sara.ai fallback, unlike the two
              states above: nothing has failed here and the receipt may still
              land, so the page offers the way back it actually knows about
              and otherwise leaves the shopper where they are. */}
          {returnUrl !== undefined && returnHost !== null && (
            <ActionLink href={returnUrl} variant="outline">
              Return to {returnHost}
            </ActionLink>
          )}
        </StatusBlock>
      )}
    </CheckoutShell>
  );
}

/**
 * The two facts the API reports, plus where the shopper is headed, as three
 * ledger rows. Rendered for every phase that involves a live session; hidden
 * entirely when there is no `session_id` to ask about, because then the page
 * genuinely knows nothing and printing three rows of "pending" would be an
 * invention.
 */
function TransactionLedger({
  phase,
  lastState,
  returnHost,
  visible,
}: {
  readonly phase: ReturnPhase;
  readonly lastState: SessionState | null;
  readonly returnHost: string | null;
  readonly visible: boolean;
}) {
  if (!visible) return null;

  const paid = lastState === "paid";
  const issued = phase === "success";
  // Once the budget is spent, everything still open is held rather than
  // merely quiet — the rows pick up the same ochre as the rail.
  const open: RowTone = phase === "timeout" ? "held" : "quiet";

  return (
    <Ledger>
      <LedgerRow
        label="Payment"
        value={paid ? "received" : "confirming"}
        tone={paid ? "settled" : open}
        delayMs={90}
      />
      <LedgerRow
        label="Receipt"
        value={issued ? "issued" : paid ? "issuing" : "pending"}
        tone={issued ? "settled" : open}
        delayMs={150}
      />
      <LedgerRow
        label="Return"
        value={returnHost ?? "pending"}
        tone={returnHost === null ? open : "quiet"}
        delayMs={210}
      />
    </Ledger>
  );
}

/** Back to the consumer app when we know it, back to sara.ai when we do not. */
function ReturnFallback({
  returnUrl,
  returnHost,
}: {
  readonly returnUrl: string | undefined;
  readonly returnHost: string | null;
}) {
  if (returnUrl !== undefined && returnHost !== null) {
    return (
      <ActionLink href={returnUrl} variant="outline">
        Return to {returnHost}
      </ActionLink>
    );
  }
  return (
    <ActionLink href="https://sara.ai" variant="outline">
      Return to sara.ai
    </ActionLink>
  );
}
